"use client";
import React, { useEffect, useState } from "react";
import ScrollRangeSelector from "../ui/ScrollRangeSelector";
import { apiGet } from "@/utils/apiClient";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { useDispatch } from "react-redux";
import { openModal } from "@/redux/features/GoldInvestModal/goldInvestModalSlice";

export function calculateMonthlySip(targetAmount: number, tenureYears: number, sipReturnPercent: number = 10): number {
  // PMT = A / (((1 + r/n)^(nt) - 1) ÷ (r/n)) × (1+r/n)

  const r = sipReturnPercent / 100;
  const n = 12;
  const t = tenureYears;

  const ratePerPeriod = r / n;
  const totalPeriods = n * t;

  const factor = ((Math.pow(1 + ratePerPeriod, totalPeriods) - 1) / ratePerPeriod) * (1 + ratePerPeriod);

  const PMT = targetAmount / factor;

  const monthlySip = PMT % 100 == 0 ? PMT : PMT + (100 - (PMT % 100));

  return monthlySip;
}

export function calculateSIPFromGrams(amount: number, tenure: number, rate: number = 10, compoundingFrequency: number = 12) {
  const r = rate / 100;
  const n = compoundingFrequency;
  const t = tenure;

  const fv = amount * Math.pow(1 + r / n, n * t);

  const monthlySip = calculateMonthlySip(fv, t);

  return monthlySip;
}

export default function GoldPriceCalc() {
  const [amount, setAmount] = useState(5000000);
  const [grams, setGrams] = useState(30);
  const [tenure, setTenure] = useState(5);
  const [sipAmount, setSIPAmount] = useState(10000);
  const [goldPricePerGram, setGoldPricePerGram] = useState(0);
  const [goldAmount, setGoldAmount] = useState(0);
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("inr");
  const [error, setError] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [gramsError, setGramsError] = useState(false);

  const MAX_GRAMS = 1000;
  const metalType = "gold";

  useEffect(() => {
    async function fetchGoldPrice() {
      try {
        const res = await apiGet("data/goldRate");

        if (!res.status) throw new Error("Something went wrong");

        const quotationUnit = res.data.PriceQuotationUnit;
        const closePrice = parseFloat(res.data.ClosePrice);

        let unitGrams = 1;

        const match = quotationUnit?.match(/(\d+)/);
        if (match) unitGrams = parseFloat(match[1]);

        const perGram = closePrice / unitGrams;

        setGoldPricePerGram(perGram);
      } catch (error) {
        console.error("Gold price fetch failed", error);
      }
    }

    fetchGoldPrice();
  }, []);

  useEffect(() => {
    const isAmountError = activeTab === "inr" && amount === 0;
    const isGramsError = activeTab !== "inr" && grams === 0;
    const isTenureError = tenure === 0;

    setAmountError(isAmountError);
    setGramsError(isGramsError);

    setError(isAmountError || isGramsError || isTenureError);
  }, [amount, grams, tenure, activeTab]);

  const pricePerGram = metalType === "gold" ? goldPricePerGram : 135.75;
  const MAX_AMOUNT = MAX_GRAMS * pricePerGram;

  const allowOnlyDigits = (str: string): string => {
    return str.replace(/[^0-9]/g, "");
  };

  const allowGramsFormat = (str: string) => {
    str = str.replace(/[^0-9.]/g, "");

    const parts = str.split(".");
    if (parts.length > 2) parts.pop();

    if (parts[1]?.length > 3) {
      parts[1] = parts[1].slice(0, 3);
    }

    return parts.join(".");
  };

  const handleAmountChange = (value: string) => {
    let cleaned = allowOnlyDigits(value);

    if (cleaned === "") {
      setAmount(0);
      return;
    }

    cleaned = cleaned.replace(/^0+/, "") || "0";
    let num = Number(cleaned);

    if (num > MAX_AMOUNT) num = MAX_AMOUNT;

    setAmount(num);
  };

  useEffect(() => {
    if (pricePerGram > 0) {
      const inrValue = grams * pricePerGram;
      setGoldAmount(inrValue);
    }
  }, [grams, pricePerGram]);

  const handleGramsChange = (value: string) => {
    const cleaned = allowGramsFormat(value);

    if (cleaned === "") {
      setGrams(0);
      return;
    }

    let num = Number(cleaned);
    if (isNaN(num)) num = 0;

    if (num > MAX_GRAMS) num = MAX_GRAMS;

    setGrams(num);
  };

  const inputValue = activeTab === "inr" ? formatIndianNumber(amount) : Number(grams);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (activeTab === "inr") {
      handleAmountChange(value);
    } else {
      handleGramsChange(value);
    }
  };

  useEffect(() => {
    if (activeTab === "inr") {
      const sipINR = calculateMonthlySip(amount, tenure);
      setSIPAmount(sipINR);
    } else {
      const sipFromGrams = calculateSIPFromGrams(goldAmount, tenure);
      setSIPAmount(sipFromGrams);
    }
  }, [amount, goldAmount, tenure, activeTab]);

  return (
    <>
      {/* SET YOUR GOAL */}
      <div className="text-center text-2sm py-2.5 mb-6 font-medium bg-[#ffffff11] uppercase">SET YOUR GOAL</div>

      <div className="flex items-center justify-between gap-3 px-3.5 mb-7">
        <label className="relative w-full">
          <span className={`absolute top-1/2 -translate-y-1/2 font-semibold transition ${activeTab === "inr" ? (amountError ? "text-[#ec4d4d]" : "text-white/50") : gramsError ? "text-[#ec4d4d]" : "text-white/50"} ${activeTab === "inr" ? "left-3" : "right-3"}`}>{activeTab === "inr" ? "₹" : "Gms"}</span>

          <input type="text" value={inputValue} onChange={handleInputChange} className={`w-full py-1 bg-transparent border-b outline-hidden transition ${activeTab === "inr" ? (amountError ? "border-[#ec4d4d] text-[#ec4d4d]" : "border-gray-500 focus:border-gray-300 hover:border-gray-300") : gramsError ? "border-[#ec4d4d] text-[#ec4d4d]" : "border-gray-500 focus:border-gray-300 hover:border-gray-300"} ${activeTab === "inr" ? "pl-7 pr-3" : "pr-7 pl-3"}`} />
        </label>

        <div className="flex items-center gap-px text-[11px] leading-4 text-center shrink-0 font-medium border rounded-full border-white/30">
          <button onClick={() => setActiveTab("inr")} className={`py-0.5 rounded-full w-16 whitespace-nowrap shrink-0 transition-all duration-300 ${activeTab === "inr" ? "text-white bg-[#FFFFFF3b] backdrop-blur-xs" : "text-[#ffffffa8] bg-transparent hover:bg-[#ffffff15]"}`}>
            In ₹
          </button>

          <button onClick={() => setActiveTab("gms")} className={`py-0.5 rounded-full w-16 whitespace-nowrap shrink-0 transition-all duration-300 ${activeTab === "gms" ? "text-white bg-[#FFFFFF3b] backdrop-blur-xs" : "text-[#ffffffa8] bg-transparent hover:bg-[#ffffff15]"}`}>
            In gm
          </button>
        </div>
      </div>

      <div className="text-center text-2sm py-2.5 mb-5 font-medium bg-[#ffffff11] uppercase">Select Duration</div>

      <ScrollRangeSelector value={tenure} onChange={(v) => setTenure(v)} />

      <div className="text-center text-2sm py-2.5 mb-4 font-medium bg-[#ffffff11] uppercase">YOUR SIP AMOUNT IS</div>
      {/* SIP AMOUNT */}
      <div className="text-center mb-6">
        <div className="text-3xl font-extrabold text-white drop-shadow-[0_0_4px_#FFD90080]">₹ {formatIndianNumber(sipAmount || 0)}/M</div>
      </div>

      {/* BUTTON */}
      <div className="p-8 pt-0">
        <button onClick={() => dispatch(openModal({ amount: sipAmount, tenure: tenure, src: "Gold Ka Goal" }))} disabled={error} title={error ? "Unable to continue. Please ensure you’ve entered valid values" : "hover:bg-[#a88100]"} className={`w-full bg-[#C89900] text-white font-medium text-base py-2 rounded-full transition-all ${error && "opacity-70"}`}>
          Proceed to Fund Selection
        </button>
      </div>
    </>
  );
}
